import React, { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import './Modal.css';

/**
 * Glacier modal shell. Replaces all Bootstrap modals in the app.
 *
 * Variants:
 *  - sheet  (mobile bottom): full-width, top-rounded 22px, grabber 38×4
 *  - card   (desktop centered): max-width 520px, all-corner-rounded, close X
 *  - drawer (desktop right): 320px-wide right rail
 *
 * Behavior:
 *  - Esc closes
 *  - Scrim click closes (clicks on the dialog itself do not)
 *  - Focus trap loops Tab / Shift+Tab through focusable children
 *  - Restores focus to the trigger element on close
 *  - Body scroll locks while open (preserves scroll position on close)
 *
 * Renders via createPortal so it escapes any clipping ancestor (chat
 * scroll containers, transformed wrappers, etc).
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const Modal = ({
  isOpen,
  onClose,
  variant = 'sheet',
  title,
  size,
  children,
  ariaLabel,
  className = '',
  // When `bare` is true, Modal skips its auto-generated title-row (title
  // text + Close button) and lets the consumer render its own chrome. The
  // scrim, Esc handler, focus trap, and scroll lock still apply — this is
  // purely a layout opt-out for surfaces that own a richer header (e.g.
  // DesktopBeam's icon + title + subtitle + custom close).
  bare = false,
}) => {
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const headingId = useId();

  // Body scroll lock: persist scrollY across open/close.
  useEffect(() => {
    if (!isOpen) return undefined;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Capture trigger on open; restore on close.
  useEffect(() => {
    if (!isOpen) return undefined;
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Move focus into the dialog after mount, but only if nothing inside
    // the dialog already has focus. This way callers (or autoFocus on a
    // child input) keep priority and we don't yank focus away from a
    // mid-keystroke target.
    const id = requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (dialog.contains(document.activeElement) && document.activeElement !== dialog) {
        return;
      }
      const focusables = dialog.querySelectorAll(FOCUSABLE_SELECTOR);
      const first = focusables[0];
      if (first instanceof HTMLElement) {
        first.focus();
      } else {
        // No focusable child — focus the dialog itself.
        dialog.focus();
      }
    });

    return () => {
      cancelAnimationFrame(id);
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (event) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const dialog = dialogRef.current;
      if (!dialog) return;

      const focusables = Array.from(
        dialog.querySelectorAll(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute('disabled'));

      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [isOpen, onClose],
  );

  // Global Esc listener — guarantees Escape closes even if focus is outside.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleScrimClick = (event) => {
    // Only close when the click target is the scrim itself, not bubble-ups
    // from inside the dialog.
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  const scrimClass = `glacier-modal__scrim glacier-modal__scrim--${variant}`;
  const dialogClass = `glacier-modal glacier-modal--${variant}${
    className ? ` ${className}` : ''
  }`;

  const labelProps = title
    ? { 'aria-labelledby': headingId }
    : { 'aria-label': ariaLabel || 'Dialog' };

  const node = (
    <div
      className={scrimClass}
      onMouseDown={handleScrimClick}
      data-testid="modal-scrim"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={dialogClass}
        data-size={size}
        onKeyDown={handleKeyDown}
        {...labelProps}
      >
        {variant === 'sheet' && <div className="glacier-modal__grabber" aria-hidden />}

        {!bare && (title || variant !== 'sheet') && (
          <div className="glacier-modal__title-row">
            {title && (
              <h2 id={headingId} className="glacier-modal__title">
                {title}
              </h2>
            )}
            {variant !== 'sheet' && (
              <button
                type="button"
                className="glacier-modal__close"
                onClick={onClose}
                aria-label="Close"
              >
                <Icon name="close" size={18} aria-hidden />
              </button>
            )}
          </div>
        )}

        {children}
      </div>
    </div>
  );

  // Portal to body so the modal escapes any clipping ancestor.
  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
};

export default Modal;
